<script setup lang="ts">
/**
 * Session host (AppLayout, renderless) — acts on what the HTTP layer reported and on sign-in:
 *  - 401 `token_revoked` (decision 15): local sign-out, the reason, the sign-in screen;
 *  - 403 `account_pending_deletion` / `account_scheduled_for_deletion` (decision 16): local sign-out, the restore screen;
 *  - the first token of a sign-in (password, OAuth, sign-up's e-mail link): the guest's language /
 *    currency go to the account once (decision 19) and the versions the sign-up checkbox showed
 *    are accepted (decision 18). A page reload with a stored token is not a sign-in.
 */
import { watch } from 'vue'
import { useAuthModal, useAuthStore, useTokenStore } from '@/features/auth/store'
import { hasProfileApi } from '@/features/profile/flags'
import { transferGuestLocaleChoice } from '@/features/survey/model/useAccountLocale'
import { queryCache } from '@/queries'
import { acceptConsents } from '../api/account'
import { consentsAcceptRequest, takePendingSignupConsents } from '../consents.data'
import { useAccountGateStore } from '../store/useAccountGateStore'

const gate = useAccountGateStore()
const tokenStore = useTokenStore()
const authStore = useAuthStore()
const authModal = useAuthModal()

watch(
  () => gate.pending,
  (pending) => {
    if (!pending) return
    const handled = gate.take()
    if (!handled) return
    authStore.logout()
    if (handled.kind === 'token_revoked' || handled.kind === 'user_not_provisioned') {
      // The reason sits on the sign-in screen itself (a toast would hide under the modal's scrim).
      gate.setSessionNotice(handled.kind)
      authModal.showAuthModal('login')
      return
    }
    authModal.showAuthModal('accountRestore')
  },
  // A 403 may arrive before the host is mounted (the first calls of the page).
  { immediate: true },
)

async function onSignedIn() {
  gate.setSessionNotice(null)
  await transferGuestLocaleChoice()
  const pending = takePendingSignupConsents()
  if (!pending.length || !hasProfileApi('consents')) return
  try {
    await acceptConsents(consentsAcceptRequest(pending, 'signup'))
  } catch (error) {
    // 409 (a version moved since the form) or offline: the banner asks again.
    console.warn('Consents: the sign-up acceptance was not recorded', error)
  } finally {
    queryCache.invalidate((key) => Array.isArray(key) && key[0] === 'consents')
  }
}

watch(
  () => tokenStore.accessToken,
  (token, previous) => {
    if (token && !previous) void onSignedIn()
  },
)
</script>

<template>
  <span hidden />
</template>
